import React, { useState, useEffect } from 'react';
import { Clock, Crown, Mail, MailCheck } from 'lucide-react';

export default function GameLimitTimer({ userEmail, onUpgradeClick }) {
  const [limitStatus, setLimitStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [emailNotifications, setEmailNotifications] = useState(false);
  const [countdown, setCountdown] = useState('');

  const API_URL = process.env.REACT_APP_BACKEND_URL;

  // Fetch game limit status
  const fetchLimitStatus = async () => {
    try {
      const response = await fetch(`${API_URL}/api/game-limits/status`, {
        headers: {
          'X-User-Email': userEmail || 'guest@example.com'
        }
      });

      if (!response.ok) throw new Error('Failed to fetch limit status');

      const data = await response.json();
      setLimitStatus(data);
      setLoading(false);

      // Get email preference from user if available
      // (This would be fetched from user profile, but we'll use local state for now)
    } catch (error) {
      console.error('Error fetching limit status:', error);
      setLoading(false);
    }
  };

  // Update countdown every second
  useEffect(() => {
    if (!limitStatus || limitStatus.is_premium) return;

    const interval = setInterval(() => {
      const seconds = limitStatus.time_until_next_game;
      const hours = Math.floor(seconds / 3600);
      const minutes = Math.floor((seconds % 3600) / 60);
      const secs = seconds % 60;

      setCountdown(
        `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`
      );

      // Decrement time
      limitStatus.time_until_next_game = Math.max(0, seconds - 1);

      // Refresh when countdown hits zero
      if (limitStatus.time_until_next_game === 0) {
        fetchLimitStatus();
      }
    }, 1000);

    return () => clearInterval(interval);
  }, [limitStatus]);

  // Initial fetch
  useEffect(() => {
    fetchLimitStatus();
  }, [userEmail]);

  // Toggle email notifications
  const toggleEmailNotifications = async () => {
    try {
      const newValue = !emailNotifications;

      const response = await fetch(`${API_URL}/api/game-limits/email-preference`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-User-Email': userEmail || 'guest@example.com'
        },
        body: JSON.stringify({
          email_notifications_enabled: newValue
        })
      });

      if (!response.ok) throw new Error('Failed to update email preference');

      setEmailNotifications(newValue);
      console.log(`Email notifications ${newValue ? 'enabled' : 'disabled'}`);
    } catch (error) {
      console.error('Error toggling email notifications:', error);
    }
  };

  if (loading) {
    return (
      <div className="bunker-panel p-4 mb-4 border border-slate-700">
        <div className="flex items-center gap-2">
          <Clock className="w-5 h-5 text-amber animate-pulse" />
          <span className="text-muted font-mono text-sm">Loading game status...</span>
        </div>
      </div>
    );
  }

  if (!limitStatus) return null;

  // Premium users - show unlimited badge
  if (limitStatus.is_premium) {
    return (
      <div className="bunker-panel p-4 mb-4 border border-yellow-500/50 bg-gradient-to-r from-yellow-900/20 to-yellow-800/20">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Crown className="w-5 h-5 text-yellow-400" />
            <span className="text-yellow-400 font-bold font-mono text-sm uppercase">Premium - Unlimited Games</span>
          </div>
          <span className="text-yellow-400 font-mono text-xs">No Ads • No Waiting</span>
        </div>
      </div>
    );
  }

  // Free users - show game limits
  const atLimit = limitStatus.games_remaining === 0;

  return (
    <div className={`bunker-panel p-4 mb-4 border ${atLimit ? 'border-red-500/50 bg-red-900/10' : 'border-green-500/50'}`}>
      {/* Games Remaining */}
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <Clock className={`w-5 h-5 ${atLimit ? 'text-red-400' : 'text-green-400'}`} />
          <span className={`font-bold font-mono text-sm ${atLimit ? 'text-red-400' : 'text-green-400'}`}>
            {limitStatus.games_remaining} / 2 GAMES REMAINING
          </span>
        </div>
        <span className="text-muted font-mono text-xs">
          {limitStatus.total_games_played} total played
        </span>
      </div>

      {/* Countdown Timer - Only show if at limit */}
      {atLimit && (
        <>
          <div className="bg-black/40 p-4 rounded border border-red-500/30 mb-3">
            <div className="text-center">
              <div className="text-xs text-muted uppercase tracking-wider mb-1">Next Game Available In:</div>
              <div className="text-3xl font-bold text-red-400 font-mono tracking-wider">
                {countdown}
              </div>
              <div className="text-xs text-muted mt-1">Hours : Minutes : Seconds</div>
            </div>
          </div>

          {/* Email Notification Checkbox */}
          <div className="flex items-center gap-2 mb-3">
            <button
              onClick={toggleEmailNotifications}
              className={`flex items-center gap-2 px-3 py-2 rounded transition-all ${
                emailNotifications
                  ? 'bg-green-600 hover:bg-green-700 text-white'
                  : 'bg-slate-700 hover:bg-slate-600 text-muted'
              }`}
            >
              {emailNotifications ? (
                <MailCheck className="w-4 h-4" />
              ) : (
                <Mail className="w-4 h-4" />
              )}
              <span className="text-xs font-mono">
                {emailNotifications ? 'Email alerts enabled' : 'Notify me when ready'}
              </span>
            </button>
          </div>

          {/* Premium Upgrade CTA */}
          <button
            onClick={onUpgradeClick}
            className="w-full bg-gradient-to-r from-yellow-500 to-yellow-600 hover:from-yellow-400 hover:to-yellow-500 text-black font-bold py-3 rounded flex items-center justify-center gap-2 transition-all"
          >
            <Crown className="w-5 h-5" />
            Upgrade to Premium - Play Unlimited
          </button>
        </>
      )}

      {/* Warning for last game */}
      {!atLimit && limitStatus.games_remaining === 1 && (
        <div className="bg-yellow-900/20 border border-yellow-500/30 p-2 rounded mt-2">
          <p className="text-yellow-400 text-xs font-mono text-center">
            ⚠️ This is your last game for 12 hours
          </p>
        </div>
      )}
    </div>
  );
}
